import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HardHat, Users, UserCog, ShieldCheck, Check, GraduationCap } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Emblem from "@/components/Emblem";
import { toast } from "sonner";

const ROLES = [
  { key: "recruit", title: "Recruit", desc: "Full access. Approve family guests.", icon: GraduationCap },
  { key: "family", title: "Family Guest", desc: "Read-only. Requires recruit approval.", icon: Users },
  { key: "recruiter", title: "Recruiter (Cadre)", desc: "Manage your profile & view your recruits.", icon: UserCog },
  { key: "admin", title: "Admin (Cadre)", desc: "Manage content & the unit. Pick your title.", icon: ShieldCheck },
];
const TITLES = [
  { v: "Commander", l: "Commander" },
  { v: "1SG", l: "First Sergeant" },
  { v: "Readiness NCO", l: "Readiness NCO" },
  { v: "Training NCO", l: "Training NCO" },
  { v: "Civilian Contractor", l: "Civilian Contractor" },
];

export default function Onboarding() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(user?.role || null);
  const [title, setTitle] = useState(user?.admin_title || "");
  const [recruitEmail, setRecruitEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [takenTitles, setTakenTitles] = useState([]);

  useEffect(() => {
    api.get("/admin-title-availability").then((r) => setTakenTitles(r.data.taken || [])).catch(() => {});
  }, []);

  const availableRoles = ROLES; // Admin (Cadre) is selectable by anyone; access requires admin approval.
  const availableTitles = TITLES.filter((t) => !takenTitles.includes(t.v) || t.v === user?.admin_title);

  const submit = async () => {
    if (!role) return toast.error("Select a role to continue.");
    if (role === "admin" && !title) return toast.error("Pick your title.");
    if (role === "family" && !recruitEmail) return toast.error("Enter your recruit's email.");
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding", {
        role,
        admin_title: role === "admin" ? title : undefined,
        recruit_email: role === "family" ? recruitEmail : undefined,
      });
      setUser(data);
      if (data.admin_approved || data.is_admin) navigate("/", { replace: true });
      else navigate("/pending", { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save role.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--rsp-bg)] flex justify-center">
      <div className="w-full max-w-[620px] px-5 py-6">
        <div className="flex items-center gap-3 mb-8">
          <Emblem size={48} glow={false} />
          <div className="leading-tight">
            <div className="eyebrow gold" style={{ letterSpacing: "0.16em" }}>RSP · Riverside</div>
            <div className="font-head uppercase tracking-[0.28em] orange text-sm font-bold">Paratus Preliator</div>
          </div>
        </div>

        <h1 className="heading text-5xl mb-1">Select your role</h1>
        <p className="text-[var(--rsp-gray)] mb-7" data-testid="signed-in-as">Signed in as {user?.email}</p>

        <div className="space-y-3">
          {availableRoles.map((r) => {
            const active = role === r.key;
            return (
              <button
                key={r.key}
                data-testid={`role-${r.key}`}
                onClick={() => setRole(r.key)}
                className={`w-full text-left rsp-card px-5 py-4 flex items-center gap-4 transition-all ${
                  active ? "border-[var(--rsp-gold)] ring-1 ring-[var(--rsp-gold)]/40" : "hover:border-[var(--rsp-muted)]"
                }`}
              >
                <r.icon size={30} className={active ? "gold" : "text-[var(--rsp-gray)]"} />
                <div className="flex-1">
                  <div className="heading text-xl">{r.title}</div>
                  <div className="text-[var(--rsp-gray)] text-sm">{r.desc}</div>
                </div>
                {active && (
                  <span className="w-7 h-7 rounded-full bg-[var(--rsp-gold)] flex items-center justify-center">
                    <Check size={16} className="text-black" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {role === "admin" && (
          <div className="mt-6" data-testid="admin-title-section">
            <div className="eyebrow mb-3" style={{ letterSpacing: "0.16em" }}>Your Title</div>
            <div className="flex flex-wrap gap-3">
              {availableTitles.map((t) => (
                <button
                  key={t.v}
                  data-testid={`title-${t.v}`}
                  onClick={() => setTitle(t.v)}
                  className={`px-5 py-2 rounded-full border font-head uppercase tracking-wide transition-colors ${
                    title === t.v ? "border-[var(--rsp-gold)] gold bg-[var(--rsp-gold)]/10" : "border-[var(--rsp-border)] text-[var(--rsp-gray)]"
                  }`}
                >
                  {t.l}
                </button>
              ))}
              {availableTitles.length === 0 && (
                <p className="text-[var(--rsp-muted)] text-sm">All admin titles are currently assigned. Contact an existing admin.</p>
              )}
            </div>
            <p className="text-[var(--rsp-muted)] text-xs mt-3">Admin access requires approval from an existing admin. Each title can be held by one person.</p>
          </div>
        )}

        {role === "family" && (
          <div className="mt-6" data-testid="family-recruit-section">
            <div className="eyebrow mb-2" style={{ letterSpacing: "0.16em" }}>Your Recruit's Email</div>
            <input
              data-testid="family-recruit-email"
              value={recruitEmail}
              onChange={(e) => setRecruitEmail(e.target.value)}
              placeholder="recruit@email.com"
              className="w-full rsp-card px-4 py-3 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)]"
            />
          </div>
        )}

        <button data-testid="onboarding-continue" onClick={submit} disabled={busy}
          className="btn-gold w-full py-4 text-lg mt-8 disabled:opacity-60">
          {busy ? "Saving…" : "Continue"}
        </button>
        <button data-testid="onboarding-signout" onClick={logout}
          className="w-full text-center text-[var(--rsp-gray)] mt-4 py-2 hover:text-[var(--rsp-cream)]">
          Sign out
        </button>
      </div>
    </div>
  );
}
